import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { commissionsService } from '../../services/commissionsService';
import CommissionPaymentModal from '../../components/commissions/CommissionPaymentModal';

type CommissionStatus = 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'DELIVERED' | 'COMPLETED' | 'CANCELLED' | 'DISPUTED';

interface Commission {
  id: string;
  creatorId: string;
  creatorName?: string;
  creatorEmail?: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  status: CommissionStatus;
  deadline?: string;
  estimatedDelivery?: string;
  createdAt: string;
  acceptedAt?: string;
  startedAt?: string;
  deliveredAt?: string;
  completedAt?: string;
  deliveryNote?: string;
  rating?: number;
  review?: string;
  deliverables?: Array<{
    id: string;
    fileName: string;
    fileUrl: string;
    uploadedAt: string;
  }>;
  stripePaymentIntentId?: string;
  stripeClientSecret?: string;
}

const PatronCommissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [commission, setCommission] = useState<Commission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchCommission();
  }, [id]);

  const fetchCommission = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await commissionsService.get(id);
      setCommission(data.commission);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load commission');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      await commissionsService.approve(id, rating, review || undefined);
      await fetchCommission();
      setShowApprovalModal(false);
      setRating(5);
      setReview('');
    } catch (err: any) {
      alert(err.message || 'Failed to approve commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    const reason = prompt('Please provide a reason for cancellation:');
    if (!reason) return;

    try {
      setActionLoading(true);
      await commissionsService.cancel(id, reason);
      await fetchCommission();
    } catch (err: any) {
      alert(err.message || 'Failed to cancel commission');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: CommissionStatus) => {
    const colors: Record<CommissionStatus, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      ACCEPTED: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-purple-100 text-purple-800',
      DELIVERED: 'bg-indigo-100 text-indigo-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      DISPUTED: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${colors[status]}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !commission) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Commission not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/commissions/requests')}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
        >
          ← Back to My Commissions
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{commission.title}</h1>
            <p className="text-gray-600 mt-1">Commission Request</p>
          </div>
          {getStatusBadge(commission.status)}
        </div>
      </div>

      {/* Payment Alert */}
      {commission.status === 'ACCEPTED' && commission.stripeClientSecret && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-orange-900">💳 Payment Authorization Required</h3>
              <p className="text-sm text-orange-800 mt-1">
                The creator has accepted your commission. Please authorize payment to allow them to start work.
                Funds will be held in escrow until you approve the final deliverables.
              </p>
            </div>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="ml-4 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 whitespace-nowrap"
            >
              Authorize Payment
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{commission.description}</p>
          </div>

          {/* Deliverables */}
          {commission.status !== 'PENDING' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Deliverables</h2>
              
              {commission.deliverables && commission.deliverables.length > 0 ? (
                <div className="space-y-3">
                  {commission.deliverables.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{file.fileName}</p>
                        <p className="text-sm text-gray-500">
                          Uploaded {formatDate(file.uploadedAt)}
                        </p>
                      </div>
                      <a
                        href={file.fileUrl}
                        download
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Download
                      </a>
                    </div>
                  ))}
                  
                  {commission.deliveryNote && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-1">Creator's Note:</p>
                      <p className="text-sm text-blue-800">{commission.deliveryNote}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">
                  {commission.status === 'IN_PROGRESS' 
                    ? 'The creator is working on your commission. Deliverables will appear here once uploaded.'
                    : 'No deliverables yet'}
                </p>
              )}
            </div>
          )}

          {/* Review (if completed) */}
          {commission.status === 'COMPLETED' && (commission.rating || commission.review) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Your Review</h2>
              {commission.rating && (
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={star <= commission.rating! ? 'text-yellow-400' : 'text-gray-300'}
                    >
                      ★
                    </span>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">({commission.rating}/5)</span>
                </div>
              )}
              {commission.review && (
                <p className="text-gray-700">{commission.review}</p>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                <div>
                  <p className="font-medium text-gray-900">Request Submitted</p>
                  <p className="text-sm text-gray-500">{formatDate(commission.createdAt)}</p>
                </div>
              </div>
              {commission.acceptedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Accepted by Creator</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.acceptedAt)}</p>
                  </div>
                </div>
              )}
              {commission.startedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-purple-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Work Started</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.startedAt)}</p>
                  </div>
                </div>
              )}
              {commission.deliveredAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-indigo-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Work Delivered</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.deliveredAt)}</p>
                  </div>
                </div>
              )}
              {commission.completedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-green-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Approved & Completed</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.completedAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Info & Actions */}
        <div className="space-y-6">
          {/* Amount */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Amount</h3>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(commission.amount, commission.currency)}
            </p>
            {commission.status === 'ACCEPTED' && (
              <p className="text-sm text-blue-600 mt-2">Held in escrow</p>
            )}
            {commission.status === 'COMPLETED' && (
              <p className="text-sm text-green-600 mt-2">Payment released to creator</p>
            )}
          </div>

          {/* Creator Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Creator</h3>
            <p className="font-medium text-gray-900">{commission.creatorName || 'Unknown'}</p>
            {commission.creatorEmail && (
              <p className="text-sm text-gray-600 mt-1">{commission.creatorEmail}</p>
            )}
          </div>

          {/* Dates */}
          {(commission.deadline || commission.estimatedDelivery) && (
            <div className="bg-white rounded-lg shadow p-6">
              {commission.deadline && (
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Requested Deadline</h3>
                  <p className="text-gray-900">{formatDate(commission.deadline)}</p>
                </div>
              )}
              {commission.estimatedDelivery && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Estimated Delivery</h3>
                  <p className="text-gray-900">{formatDate(commission.estimatedDelivery)}</p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Actions</h3>
            <div className="space-y-2">
              {commission.status === 'DELIVERED' && (
                <button
                  onClick={() => setShowApprovalModal(true)}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Approve & Release Payment
                </button>
              )}
              {['PENDING', 'ACCEPTED'].includes(commission.status) && (
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Cancel Request
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Approve Commission</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating (1-5 stars)
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="text-3xl focus:outline-none"
                  >
                    <span className={star <= rating ? 'text-yellow-400' : 'text-gray-300'}>
                      ★
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review (Optional)
              </label>
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Share your experience with this creator..."
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                ⚠️ Once approved, the payment will be released to the creator and cannot be reversed.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Approve & Release Payment
              </button>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && commission.stripeClientSecret && (
        <CommissionPaymentModal
          onClose={() => setShowPaymentModal(false)}
          commissionId={commission.id}
          clientSecret={commission.stripeClientSecret}
          onSuccess={() => {
            setShowPaymentModal(false);
            fetchCommission();
          }}
        />
      )}
    </div>
  );
};

export default PatronCommissionDetailPage;
